import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, setDoc, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import * as XLSX from 'xlsx';

interface Teacher {
  teacherId: string;
  name: string;
  department: string;
  status: string;
}

interface AttendanceRecord {
  teacherId: string;
  name: string;
  department: string;
  status: 'present' | 'absent' | 'leave' | 'half-day';
}

interface TeacherAttendanceProps {
  domain: string;
}

const TeacherAttendance: React.FC<TeacherAttendanceProps> = ({ domain }) => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [attendance, setAttendance] = useState<Map<string, AttendanceRecord>>(new Map());
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Export states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadTeachers();
  }, [domain]);

  useEffect(() => {
    if (teachers.length > 0) {
      loadAttendance();
    }
  }, [selectedDate, teachers]);

  const loadTeachers = async () => {
    setLoading(true);
    try {
      const teachersRef = collection(db, `school-users/${domain}/teachers`);
      const q = query(teachersRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);

      const teachersList: Teacher[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        teachersList.push({
          teacherId: data.teacherId,
          name: data.name,
          department: data.department || 'N/A',
          status: data.status,
        });
      });

      teachersList.sort((a, b) => a.name.localeCompare(b.name));
      setTeachers(teachersList);

      // Initialize attendance with default 'present' status
      const initialAttendance = new Map<string, AttendanceRecord>();
      teachersList.forEach(teacher => {
        initialAttendance.set(teacher.teacherId, {
          teacherId: teacher.teacherId,
          name: teacher.name,
          department: teacher.department,
          status: 'present',
        });
      });
      setAttendance(initialAttendance);
    } catch (error) {
      console.error('Error loading teachers:', error);
      alert('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const loadAttendance = async () => {
    try {
      const attendanceRef = collection(db, `school-users/${domain}/teacher-attendance`);
      const q = query(attendanceRef, where('date', '==', selectedDate));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const loadedAttendance = new Map<string, AttendanceRecord>();
        snapshot.forEach((doc) => {
          const data = doc.data();
          loadedAttendance.set(data.teacherId, {
            teacherId: data.teacherId,
            name: data.name,
            department: data.department,
            status: data.status,
          });
        });
        setAttendance(loadedAttendance);
      } else {
        // Reset to default 'present' if no attendance record exists
        const initialAttendance = new Map<string, AttendanceRecord>();
        teachers.forEach(teacher => {
          initialAttendance.set(teacher.teacherId, {
            teacherId: teacher.teacherId,
            name: teacher.name,
            department: teacher.department,
            status: 'present',
          });
        });
        setAttendance(initialAttendance);
      }
    } catch (error) {
      console.error('Error loading attendance:', error);
    }
  };

  const handleAttendanceChange = (teacherId: string, status: 'present' | 'absent' | 'leave' | 'half-day') => {
    const updatedAttendance = new Map(attendance);
    const record = updatedAttendance.get(teacherId);
    if (record) {
      record.status = status;
      updatedAttendance.set(teacherId, record);
      setAttendance(updatedAttendance);
    }
  };

  const markAllPresent = () => {
    const updatedAttendance = new Map(attendance);
    updatedAttendance.forEach((record) => {
      record.status = 'present';
    });
    setAttendance(new Map(updatedAttendance));
  };

  const submitAttendance = async () => {
    setSubmitting(true);
    setSubmitSuccess(false);

    try {
      const batch: Promise<void>[] = [];

      attendance.forEach((record) => {
        const docId = `${record.teacherId}_${selectedDate}`.replace(/\s+/g, '_');
        const docRef = doc(db, `school-users/${domain}/teacher-attendance`, docId);

        const attendanceData = {
          teacherId: record.teacherId,
          name: record.name,
          department: record.department,
          date: selectedDate,
          status: record.status,
          markedAt: new Date().toISOString(),
          markedBy: 'admin',
        };

        batch.push(setDoc(docRef, attendanceData));
      });

      await Promise.all(batch);

      setSubmitSuccess(true);
      console.log('✅ Attendance submitted successfully');
    } catch (error: any) {
      console.error('Error submitting attendance:', error);
      alert(`Failed to submit attendance: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredTeachers = teachers.filter(teacher =>
    teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.teacherId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getAttendanceSummary = () => {
    const summary = {
      present: 0,
      absent: 0,
      leave: 0,
      halfDay: 0,
      total: attendance.size,
    };

    attendance.forEach((record) => {
      switch (record.status) {
        case 'present':
          summary.present++;
          break;
        case 'absent':
          summary.absent++;
          break;
        case 'leave':
          summary.leave++;
          break;
        case 'half-day':
          summary.halfDay++;
          break;
      }
    });

    return summary;
  };

  const summary = getAttendanceSummary();

  // Export attendance to Excel for date range
  const exportToExcel = async () => {
    if (!exportStartDate || !exportEndDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(exportStartDate) > new Date(exportEndDate)) {
      alert('Start date cannot be after end date');
      return;
    }

    setExporting(true);
    try {
      // Fetch all attendance records for the date range
      const attendanceRef = collection(db, `school-users/${domain}/teacher-attendance`);
      const snapshot = await getDocs(attendanceRef);

      // Filter records within date range
      const records: Array<{
        teacherId: string;
        name: string;
        department: string;
        date: string;
        status: string;
      }> = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.date >= exportStartDate && data.date <= exportEndDate) {
          records.push({
            teacherId: data.teacherId,
            name: data.name,
            department: data.department,
            date: data.date,
            status: data.status,
          });
        }
      });

      if (records.length === 0) {
        alert('No attendance records found for the selected date range');
        setExporting(false);
        return;
      }

      // Get unique dates and sort them
      const uniqueDates = [...new Set(records.map(r => r.date))].sort();

      // Get unique teachers
      const teacherMap = new Map<string, { teacherId: string; name: string; department: string }>();
      records.forEach(r => {
        if (!teacherMap.has(r.teacherId)) {
          teacherMap.set(r.teacherId, {
            teacherId: r.teacherId,
            name: r.name,
            department: r.department,
          });
        }
      });
      const uniqueTeachers = Array.from(teacherMap.values()).sort((a, b) => a.name.localeCompare(b.name));

      // Create attendance lookup
      const attendanceLookup = new Map<string, string>();
      records.forEach(r => {
        attendanceLookup.set(`${r.teacherId}_${r.date}`, r.status);
      });

      // Build Excel data
      const excelData: Array<Record<string, string>> = [];

      uniqueTeachers.forEach(teacher => {
        const row: Record<string, string> = {
          'Teacher ID': teacher.teacherId,
          'Name': teacher.name,
          'Department': teacher.department,
        };

        // Add attendance for each date
        let presentCount = 0;
        let absentCount = 0;
        let leaveCount = 0;
        let halfDayCount = 0;

        uniqueDates.forEach(date => {
          const status = attendanceLookup.get(`${teacher.teacherId}_${date}`) || '-';
          row[date] = status.charAt(0).toUpperCase() + status.slice(1);

          if (status === 'present') presentCount++;
          else if (status === 'absent') absentCount++;
          else if (status === 'leave') leaveCount++;
          else if (status === 'half-day') halfDayCount++;
        });

        // Add summary columns
        row['Present'] = String(presentCount);
        row['Absent'] = String(absentCount);
        row['Leave'] = String(leaveCount);
        row['Half Day'] = String(halfDayCount);
        row['Total Days'] = String(uniqueDates.length);
        row['Attendance %'] = uniqueDates.length > 0
          ? ((presentCount + halfDayCount * 0.5) / uniqueDates.length * 100).toFixed(1) + '%'
          : '0%';

        excelData.push(row);
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Teacher Attendance');

      // Auto-size columns
      const colWidths = Object.keys(excelData[0] || {}).map(key => ({
        wch: Math.max(key.length, 12)
      }));
      worksheet['!cols'] = colWidths;

      // Generate filename
      const filename = `Teacher_Attendance_${exportStartDate}_to_${exportEndDate}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);

      alert(`Attendance report exported successfully!\nFile: ${filename}`);
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting attendance:', error);
      alert('Failed to export attendance. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Open export modal with default date range (current month)
  const openExportModal = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setExportStartDate(firstDay.toISOString().split('T')[0]);
    setExportEndDate(today.toISOString().split('T')[0]);
    setShowExportModal(true);
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-calendar-check me-2 text-primary-custom"></i>
          Teacher Attendance
        </h5>
        <small className="text-muted">Mark daily attendance for teaching staff</small>
      </div>
      <div className="card-body">
        {/* Date Selection and Summary */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <label className="form-label fw-semibold">
              <i className="bi bi-calendar-date me-2"></i>
              Select Date
            </label>
            <input
              type="date"
              className="form-control"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          <div className="col-md-8">
            <label className="form-label fw-semibold">Summary</label>
            <div className="d-flex gap-3">
              <div className="badge bg-success fs-6 px-3 py-2">
                <i className="bi bi-check-circle me-1"></i>
                Present: {summary.present}
              </div>
              <div className="badge bg-danger fs-6 px-3 py-2">
                <i className="bi bi-x-circle me-1"></i>
                Absent: {summary.absent}
              </div>
              <div className="badge bg-warning text-dark fs-6 px-3 py-2">
                <i className="bi bi-calendar-x me-1"></i>
                Leave: {summary.leave}
              </div>
              <div className="badge bg-info fs-6 px-3 py-2">
                <i className="bi bi-clock me-1"></i>
                Half Day: {summary.halfDay}
              </div>
            </div>
          </div>
        </div>

        {/* Search and Quick Actions */}
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, ID, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-6 text-end">
            <button className="btn btn-outline-success me-2" onClick={openExportModal}>
              <i className="bi bi-file-earmark-excel me-2"></i>
              Export to Excel
            </button>
            <button className="btn btn-outline-primary" onClick={markAllPresent}>
              <i className="bi bi-check-all me-2"></i>
              Mark All Present
            </button>
          </div>
        </div>

        {/* Attendance List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-3">Loading teachers...</p>
          </div>
        ) : teachers.length === 0 ? (
          <div className="alert alert-warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            No active teachers found. Please upload teachers first using Bulk Upload.
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <table className="table table-hover">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Teacher ID</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Attendance Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeachers.map((teacher) => {
                  const record = attendance.get(teacher.teacherId);
                  const currentStatus = record?.status || 'present';

                  return (
                    <tr key={teacher.teacherId}>
                      <td>{teacher.teacherId}</td>
                      <td>{teacher.name}</td>
                      <td>{teacher.department}</td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          <button
                            type="button"
                            className={`btn ${currentStatus === 'present' ? 'btn-success' : 'btn-outline-success'}`}
                            onClick={() => handleAttendanceChange(teacher.teacherId, 'present')}
                          >
                            <i className="bi bi-check-circle me-1"></i>
                            Present
                          </button>
                          <button
                            type="button"
                            className={`btn ${currentStatus === 'absent' ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={() => handleAttendanceChange(teacher.teacherId, 'absent')}
                          >
                            <i className="bi bi-x-circle me-1"></i>
                            Absent
                          </button>
                          <button
                            type="button"
                            className={`btn ${currentStatus === 'leave' ? 'btn-warning' : 'btn-outline-warning'}`}
                            onClick={() => handleAttendanceChange(teacher.teacherId, 'leave')}
                          >
                            <i className="bi bi-calendar-x me-1"></i>
                            Leave
                          </button>
                          <button
                            type="button"
                            className={`btn ${currentStatus === 'half-day' ? 'btn-info' : 'btn-outline-info'}`}
                            onClick={() => handleAttendanceChange(teacher.teacherId, 'half-day')}
                          >
                            <i className="bi bi-clock me-1"></i>
                            Half Day
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Success Message */}
        {submitSuccess && (
          <div className="alert alert-success mt-4">
            <i className="bi bi-check-circle me-2"></i>
            Attendance for {selectedDate} submitted successfully!
          </div>
        )}

        {/* Submit Button */}
        {teachers.length > 0 && (
          <div className="mt-4">
            <button
              className="btn btn-primary btn-lg"
              onClick={submitAttendance}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-2"></i>
                  Submit Attendance ({summary.total} teachers)
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="bi bi-file-earmark-excel me-2"></i>
                  Export Attendance to Excel
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowExportModal(false)}
                  disabled={exporting}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-4">
                  Select a date range to export teacher attendance data. The Excel file will include daily attendance status and summary statistics for each teacher.
                </p>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-calendar-event me-2"></i>
                      Start Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={exportStartDate}
                      onChange={(e) => setExportStartDate(e.target.value)}
                      disabled={exporting}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-calendar-event me-2"></i>
                      End Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={exportEndDate}
                      onChange={(e) => setExportEndDate(e.target.value)}
                      disabled={exporting}
                    />
                  </div>
                </div>

                <div className="alert alert-info mt-4 mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Export includes:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Teacher ID, Name, Department</li>
                    <li>Daily attendance status for each date</li>
                    <li>Summary: Present, Absent, Leave, Half Day counts</li>
                    <li>Attendance percentage calculation</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowExportModal(false)}
                  disabled={exporting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={exportToExcel}
                  disabled={exporting || !exportStartDate || !exportEndDate}
                >
                  {exporting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-download me-2"></i>
                      Download Excel
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAttendance;
